import React from 'react'
import { Table, Button, Tag, Space, Avatar } from 'antd'
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Product } from '../types/product.types'
import { DocumentAvatar } from '../../../shared/components/DocumentAvatar'

interface ProductsTableProps {
    products: Product[]
    onRowClick: (product: Product) => void
    onEdit: (product: Product) => void
    onDelete: (product: Product) => void
}

export const ProductsTable: React.FC<ProductsTableProps> = ({
    products,
    onRowClick,
    onEdit,
    onDelete
}) => {
    const columns: ColumnsType<Product> = [
        {
            title: 'Image',
            dataIndex: 'imageUrl',
            key: 'imageUrl',
            render: (url: string, record: Product) => (
                record.imageId ? (
                    <DocumentAvatar 
                        documentId={record.imageId} 
                        shape="square" 
                        size={40} 
                        fallback={<div style={{ width: 40, height: 40, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>-</div>} 
                    />
                ) : url ? (
                    <Avatar src={url} shape="square" size={40} />
                ) : (
                    <Avatar shape="square" size={40} style={{ backgroundColor: '#f5f5f5', color: '#bfbfbf' }}>-</Avatar>
                )
            ),
        },
        {
            title: 'SKU',
            dataIndex: 'sku',
            key: 'sku',
            render: (sku: string) => <strong>{sku}</strong>,
        },
        {
            title: 'Product Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (category: string) => <Tag color="blue">{category}</Tag>,
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
        },

        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colors = {
                    active: 'green',
                    inactive: 'gray',
                }
                return <Tag color={colors[status as keyof typeof colors]}>{status.replace('_', ' ').toUpperCase()}</Tag>
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onRowClick(record)}>
                        View
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit(record)
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(record)
                        }}
                    >
                        Delete
                    </Button>
                </Space>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={products}
            rowKey="id"
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            pagination={{
                total: products.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} products`,
            }}
        />
    )
}
